import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Bell, 
  CheckCircle, 
  Trophy, 
  Users, 
  MessageCircle,
  BookOpen,
  Trash2,
  Loader2,
  Gift,
  AlertCircle
} from 'lucide-react';
import { FadeIn, AnimatedCard } from '../components/ui/AnimatedCard';
import { useNotifications, useMarkAsRead, useMarkAllAsRead, useDeleteNotification } from '../hooks/useNotifications';
import { useAuthStore } from '../stores/authStore';

const getIcon = (type: string) => {
  switch (type) {
    case 'exam_reminder': return BookOpen;
    case 'result': return Trophy;
    case 'badge': return Trophy;
    case 'friend_request': return Users;
    case 'group_message': return MessageCircle;
    case 'system': return Gift;
    default: return Bell;
  }
};

const getIconColor = (type: string) => {
  switch (type) {
    case 'exam_reminder': return 'bg-primary-100 text-primary-600';
    case 'result': return 'bg-green-100 text-green-600';
    case 'badge': return 'bg-amber-100 text-amber-600';
    case 'friend_request': return 'bg-secondary-100 text-secondary-600';
    case 'group_message': return 'bg-blue-100 text-blue-600';
    case 'system': return 'bg-purple-100 text-purple-600';
    default: return 'bg-slate-100 text-slate-600';
  }
};

function formatTime(dateStr: string) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
}

export function Notifications() {
  const { isAuthenticated } = useAuthStore();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  
  const { data: notifications, isLoading } = useNotifications(50);
  const markAsRead = useMarkAsRead();
  const markAllAsRead = useMarkAllAsRead();
  const deleteNotification = useDeleteNotification();

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-500">Please login to view notifications</p>
      </div>
    );
  }

  const filteredNotifications = filter === 'unread' 
    ? notifications?.filter((n: any) => !n.isRead) || []
    : notifications || [];

  const unreadCount = notifications?.filter((n: any) => !n.isRead).length || 0;

  return (
    <div className="max-w-2xl mx-auto space-y-6 px-4 py-8">
      {/* Header */}
      <FadeIn>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bell className="w-8 h-8 text-primary-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Notifications</h1>
              <p className="text-sm text-slate-500">Stay updated with your activities</p>
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50"
            >
              {markAllAsRead.isPending ? 'Marking...' : 'Mark all as read'}
            </button>
          )}
        </div>
      </FadeIn>

      {/* Filter Tabs */}
      <FadeIn delay={0.1}>
        <div className="flex gap-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All ({notifications?.length || 0})
          </button>
          <button
            onClick={() => setFilter('unread')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === 'unread'
                ? 'bg-primary-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            Unread ({unreadCount})
          </button>
        </div>
      </FadeIn>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* Notifications List */}
      {!isLoading && (
        <div className="space-y-3">
          <AnimatePresence>
            {filteredNotifications.map((notification: any) => {
              const Icon = getIcon(notification.type);
              return (
                <motion.div
                  key={notification.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  layout
                >
                  <AnimatedCard 
                    className={`p-4 ${!notification.isRead ? 'border-primary-300 bg-primary-50/30' : ''}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getIconColor(notification.type)}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className={`font-semibold ${!notification.isRead ? 'text-slate-900' : 'text-slate-700'}`}>
                              {notification.title}
                            </h3>
                            <p className="text-sm text-slate-500 mt-0.5">{notification.message}</p>
                            <span className="text-xs text-slate-400 mt-1">{formatTime(notification.createdAt)}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {!notification.isRead && (
                              <button
                                onClick={() => markAsRead.mutate(notification.id)}
                                disabled={markAsRead.isPending}
                                className="p-1.5 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors"
                                title="Mark as read"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification.mutate(notification.id)}
                              disabled={deleteNotification.isPending}
                              className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AnimatedCard>
                </motion.div>
              );
            })}
          </AnimatePresence>

          {filteredNotifications.length === 0 && (
            <FadeIn>
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-500">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </h3>
                <p className="text-sm text-slate-400 mt-2">
                  Complete exams and activities to get notifications
                </p>
              </div>
            </FadeIn>
          )}
        </div>
      )}
    </div>
  );
}
